import React, { useEffect, useRef, useState } from "react";

import {
  Button,
  Card,
  Col,
  DatePicker,
  Dropdown,
  InputNumber,
  Menu,
  message,
  Modal,
  Row,
  Select,
  Space,
  Statistic,
  Table,
  Tag,
  Typography,
  Upload,
} from "antd";
import fileDownload from "js-file-download";
import moment from "moment";
import PropTypes from "prop-types";

import {
  DownloadOutlined,
  MoreOutlined,
  UploadOutlined,
} from "@ant-design/icons";

import CustomSearchbar from "../../Components/CustomSearchbar/CustomSearchbar";
import ManagePlanModal from "../../Components/ManagePlanModal";
import ManageTagUntagPartnerModal from "../../Components/ManageTagUntagPartnerModal";
import RefreshEarningsButton from "../../Components/RefreshEarningsButton/RefreshEarningsButton";
import TableUserInfoItem from "../../Components/TableUserInfoItem/TableUserInfoItem";
import UpdateGstDetailsModal from "../../Components/UpdateGstDetailsModal";
import { useAppTheme } from "../../context/ThemeProvider";
import useDebounce from "../../hooks/useDebounce";
import { useAppDispatch, useAppSelector } from "../../store/hook";
import { fetchManagers } from "../../store/Thunks/appThunk";
import {
  AUTOPAY_STATUS_MAP,
  DATE_TIME_FORMAT,
  DEFAULT_DATES_RANGE_OPTIONS,
  DOMAIN_STATUS_MAP,
  PAYMENT_PLAN_COLORS,
  USER_TIER_TITLE,
} from "../../Utils/constants";
import { showAppError } from "../../Utils/error";
import { numFormatter } from "../../Utils/formatter";
import Loader from "../../Utils/Loader";
import debounce from "../../Utils/utilityFunctions";
import {
  cancelTierSubscription,
  getWhitelabelCreatorsData,
  suspendPlatform,
  updateWLExpiry,
  upgradeTier,
  uploadBulkAccountManager,
  validateBulkAccountManager,
} from "../api";
import CreatorDetailExpand from "./CreatorDetailExpand";

const { Option } = Select;

const EnterpriseCreators = ({ sensitiveAllowedActions }) => {
  const { COLORS } = useAppTheme();
  const dispatch = useAppDispatch();
  const { managerState } = useAppSelector((state) => state.app);
  const { managers } = managerState;

  const [users, setUsers] = useState([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [earningGt, setEarningGt] = useState(null);
  const [earningLt, setEarningLt] = useState(null);
  const [mangoCountGt, setMangoCountGt] = useState(null);
  const [mangoCountLt, setMangoCountLt] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [tierUpgradeModalVisible, setTierUpgradeModalVisible] = useState(false);
  const [selectedTier, setSelectedTier] = useState(null);
  const [selectPaymentPlan, setSelectPaymentPlan] = useState(null);
  const [filterTier, setFilterTier] = useState(null);
  const [filterDomainStatus, setFilterDomainStatus] = useState(null);
  const [totalDomainActivated, setTotalDomainActivated] = useState(0);
  const [totalSuspended, setTotalSuspended] = useState(0);
  const [tierExpiryStartDate, setTierExpiryStartDate] = useState(null);
  const [tierExpiryEndDate, setTierExpiryEndDate] = useState(null);
  const [suspendPlatformModalVisible, setSuspendPlatformModalVisible] =
    useState(false);
  const [tierExpiryStatus, setTierExpiryStatus] = useState(null);
  const [tierExpiryUpdateModalVisible, setTierExpiryUpdateModalVisible] =
    useState(false);
  const [selectedTierExpiryDate, setSelectedTierExpiryDate] = useState(null);
  const [platformSuspensionStatus, setPlatformSuspensionStatus] =
    useState(null);
  const [tierAutopayStatus, setTierAutopayStatus] = useState(null);
  const [cancelSubscriptionModalVisible, setCancelSubscriptionModalVisible] =
    useState(false);
  const [onetimePer, setOnetimePer] = useState(null);
  const [recurringPer, setRecurringPer] = useState(null);
  const debouncedSearch = useDebounce({
    value: search,
    callback: () => setPage(1),
  });
  const [tableKey, setTableKey] = useState(Date.now());
  const [performSuspendPlatform, setPerformSuspendPlatform] = useState(false);
  const [managePlanModalVisible, setManagePlanModalVisible] = useState(false);
  const [partnerTagUntagModalVisible, setPartnerTagUntagModalVisible] =
    useState(false);
  const [gstDetailsModalVisible, setGstDetailsModalVisible] = useState(false);
  const [accountManagerCSVModalVisible, setAccountManagerCSVModalVisible] =
    useState(false);
  const [csvUploadLoading, setCsvUploadLoading] = useState(false);
  const [csvData, setCsvData] = useState(null);
  const [validationResult, setValidationResult] = useState(null);
  const [csvDownloadLoading, setCsvDownloadLoading] = useState(false);
  const controllerRef = useRef(null);
  // const [accountManagers, setAccountManagers] = useState([]);
  const [selectedAccountManager, setSelectedAccountManager] = useState(null);

  const autopayStatuses = [
    "pending",
    "halted",
    "cancelled",
    "paused",
    "expired",
    "completed",
    "active",
    "authenticated",
  ];

  const sensitiveAllowedFunctionSet = React.useMemo(
    () => new Set(sensitiveAllowedActions),
    [sensitiveAllowedActions]
  );

  const creatorsTable = () => {
    const columns = [
      {
        title: "Creator",
        dataIndex: "name",
        key: "name",
        render: (_, record) => {
          return (
            <TableUserInfoItem
              name={record?.name}
              profilePic={record?.profilePicUrl}
              hideContact
            />
          );
        },
      },
      {
        title: "Host",
        dataIndex: "host",
        key: "host",
        render: (text, record) => (
          <>
            <Typography.Link href={`https://${text}`}>{text}</Typography.Link>
            <Typography>
              <strong style={{ marginRight: 8 }}>Domain status:</strong>
              <Tag color={DOMAIN_STATUS_MAP[record?.domainStatus]?.color}>
                {DOMAIN_STATUS_MAP[record?.domainStatus]?.text}
              </Tag>
            </Typography>
            <Typography>
              <strong style={{ marginRight: 8 }}>
                Domain status updated on:
              </strong>
              <span>
                {record?.domainStatusUpdatedAt
                  ? moment(record?.domainStatusUpdatedAt).format(
                      DATE_TIME_FORMAT
                    )
                  : "-"}
              </span>
            </Typography>
          </>
        ),
      },
      {
        title: "User Tier",
        dataIndex: "userTier",
        key: "userTier",
        render: (userTier) => (
          <span style={{ whiteSpace: "nowrap" }}>
            {USER_TIER_TITLE?.[userTier] || "-"}
          </span>
        ),
      },
      {
        title: "Payment Plan",
        dataIndex: "paymentPlan",
        key: "paymentPlan",
        render: (paymentPlan) => {
          return (
            <Tag color={PAYMENT_PLAN_COLORS?.[paymentPlan] || "default"}>
              {paymentPlan}
            </Tag>
          );
        },
      },

      {
        title: "Account Manager",
        dataIndex: "accountManagerName",
        key: "accountManagerName",
        render: (_, record) => (
          <span style={{ whiteSpace: "nowrap" }}>
            {record?.accountManagerName || "-"}
            <br />
            {record?.accountManagerEmail || "-"}
          </span>
        ),
      },
      {
        title: "Mango Count",
        dataIndex: "mangoCount",
        key: "mangoCount",
        render: (mangoCount) =>
          numFormatter({ amount: mangoCount, hideCurrency: true }),
      },
      {
        title: "Total Earnings",
        dataIndex: "totalEarning",
        key: "totalEarning",
        render: (totalEarning) => numFormatter({ amount: totalEarning }),
      },
      {
        title: "Created On",
        dataIndex: "createdAt",
        key: "createdAt",
        render: (date) => (date ? moment(date).format(DATE_TIME_FORMAT) : "-"),
      },
      {
        title: "Tier Expiry Date",
        dataIndex: "accountSettingTierSubscriptionExpiry",
        key: "accountSettingTierSubscriptionExpiry",
        render: (date, record) => (
          <Space direction="vertical">
            {date ? (
              <Typography>{moment(date).format(DATE_TIME_FORMAT)}</Typography>
            ) : null}
            <Tag color={record?.tierExpired ? "error" : "success"}>
              {record?.tierExpired ? "Tier Expired" : "Tier Active"}
            </Tag>
          </Space>
        ),
      },
      {
        title: "Tier Autopay Status",
        dataIndex: "accountSettingTierRazorpaySubscriptionIsAutopayStatus",
        key: "accountSettingTierRazorpaySubscriptionIsAutopayStatus",
        render: (status) =>
          status ? (
            <Tag color={AUTOPAY_STATUS_MAP[status]?.color}>
              {AUTOPAY_STATUS_MAP[status]?.text}
            </Tag>
          ) : (
            "-"
          ),
      },
      {
        title: "Whatsapp Autopay Status",
        dataIndex: "accountSettingWhatsappRazorpaySubscriptionIsAutopayStatus",
        key: "accountSettingWhatsappRazorpaySubscriptionIsAutopayStatus",
        render: (status) =>
          status ? (
            <Tag color={AUTOPAY_STATUS_MAP[status]?.color}>
              {AUTOPAY_STATUS_MAP[status]?.text}
            </Tag>
          ) : (
            "-"
          ),
      },
      {
        title: "Platform Status",
        dataIndex: "platformSuspended",
        key: "platformSuspended",
        render: (status) => (
          <Tag color={status ? "error" : "success"}>
            {status ? "Suspended" : "Active"}
          </Tag>
        ),
      },
      {
        title: "Actions",
        key: "actions",
        render: (_, record) => {
          const tier234 =
            record?.userTier === "tier2" ||
            record?.userTier === "tier3" ||
            record?.userTier === "tier4";

          const onRowActionMenuClick = ({ key, domEvent }) => {
            domEvent?.stopPropagation?.();
            setSelectedUser(record);
            if (record?.platformSuspended) {
              if (key === "activate-platform") {
                setPerformSuspendPlatform(false);
                setSuspendPlatformModalVisible(true);
              } else if (key === "update-gst") {
                setGstDetailsModalVisible(true);
              }
              return;
            }
            switch (key) {
              case "update-tier-expiry":
                setTierExpiryUpdateModalVisible(true);
                break;
              case "cancel-subscription":
                setCancelSubscriptionModalVisible(true);
                break;
              case "upgrade-tier":
                setTierUpgradeModalVisible(true);
                setOnetimePer(record?.razorpayOnetimeComission);
                setRecurringPer(record?.razorpayRecurringComission);
                setSelectedTier(record?.userTier);
                setSelectPaymentPlan(record?.paymentPlan);
                break;
              case "suspend-platform":
                setPerformSuspendPlatform(true);
                setSuspendPlatformModalVisible(true);
                break;
              case "manage-plan":
                setManagePlanModalVisible(true);
                break;
              case "partner-tag":
                setPartnerTagUntagModalVisible(true);
                break;
              case "update-gst":
                setGstDetailsModalVisible(true);
                break;
              default:
                break;
            }
          };

          const overlay = record?.platformSuspended ? (
            <Menu onClick={onRowActionMenuClick}>
              <Menu.Item key="activate-platform">Activate platform</Menu.Item>
              <Menu.Item key="update-gst">Update GST</Menu.Item>
            </Menu>
          ) : (
            <Menu onClick={onRowActionMenuClick}>
              {tier234 ? (
                <>
                  <Menu.Item key="update-tier-expiry" disabled>
                    Update tier expiry
                  </Menu.Item>
                  <Menu.Item key="cancel-subscription" disabled danger>
                    Cancel subscription
                  </Menu.Item>
                </>
              ) : null}
              <Menu.Item key="upgrade-tier" disabled>
                Upgrade tier
              </Menu.Item>
              <Menu.Item key="suspend-platform" danger>
                Suspend platform
              </Menu.Item>
              {sensitiveAllowedFunctionSet.has("managePlanButton") ? (
                <Menu.Item key="manage-plan">Manage plan</Menu.Item>
              ) : null}
              <Menu.Item key="partner-tag">Tag/Untag partner</Menu.Item>
              {sensitiveAllowedFunctionSet.has("updateGstDetails") ? (
                <Menu.Item key="update-gst">Update GST</Menu.Item>
              ) : null}
            </Menu>
          );

          return (
            <div onClick={(e) => e.stopPropagation()} role="presentation">
              <Dropdown
                overlay={overlay}
                trigger={["click"]}
                placement="bottomRight"
              >
                <Button
                  type="text"
                  shape="circle"
                  icon={<MoreOutlined />}
                  aria-label="Open row actions"
                  className="siteBtn circularBgHover gray"
                  onClick={(e) => e.stopPropagation()}
                />
              </Dropdown>
            </div>
          );
        },
      },
    ];

    return (
      <Table
        key={tableKey}
        columns={columns}
        dataSource={users}
        pagination={{
          current: page,
          total,
          onChange: (p) => setPage(p),
          showSizeChanger: false,
        }}
        loading={loading}
        rowKey={(record) => record._id}
        expandable={{
          expandedRowRender: (record) => (
            <CreatorDetailExpand
              creatorId={record?._id}
              canManageSignupService={sensitiveAllowedFunctionSet.has(
                "manageSignupService"
              )}
            />
          ),
        }}
        scroll={{ x: "auto" }}
      />
    );
  };

  const handleGetWLCreators = async () => {
    setLoading(true);
    try {
      controllerRef.current = new AbortController();
      const resp = await getWhitelabelCreatorsData(
        {
          search: debouncedSearch,
          whitelabelPlanType: "enterprise-plan",
          tier: filterTier,
          domainActivationStatus: filterDomainStatus,
          startDate,
          endDate,
          earningGt,
          earningLt,
          mangoCountGt,
          mangoCountLt,
          tierExpiryDateGt: tierExpiryStartDate,
          tierExpiryDateLt: tierExpiryEndDate,
          tierExpired: tierExpiryStatus,
          platformSuspensionStatus,
          tierAutopayStatus,
          accountManager: selectedAccountManager,
          page,
          limit: 10,
          csv: false,
        },
        { signal: controllerRef?.current?.signal }
      );

      if (resp.code === 0) {
        controllerRef.current = null;
        setUsers(resp?.result?.data);
        setTotal(resp?.result?.total);
        setTotalDomainActivated(resp?.result?.totalDomainActivated);
        setTotalSuspended(resp?.result?.totalSuspended);
        setTableKey(Date.now());
      } else {
        showAppError(resp);
      }
    } catch (err) {
      console.log(err);
      showAppError(err);
    } finally {
      setLoading(false);
    }
  };

  const handleGetWLCreatorsCSV = async () => {
    try {
      setCsvDownloadLoading(true);
      const resp = await getWhitelabelCreatorsData({
        search: debouncedSearch,
        whitelabelPlanType: "enterprise-plan",
        tier: filterTier,
        domainActivationStatus: filterDomainStatus,
        startDate,
        endDate,
        earningGt,
        earningLt,
        mangoCountGt,
        mangoCountLt,
        tierExpiryDateGt: tierExpiryStartDate,
        tierExpiryDateLt: tierExpiryEndDate,
        tierExpired: tierExpiryStatus,
        platformSuspensionStatus,
        tierAutopayStatus,
        accountManager: selectedAccountManager,
        page,
        limit: 10,
        csv: true,
      });

      fileDownload(resp, `enterprise-creators-${Date.now()}.csv`);
    } catch (err) {
      console.log(err);
      message.error("Something went wrong");
    } finally {
      setCsvDownloadLoading(false);
    }
  };

  const handleTierUpgrade = async (
    userId,
    tier,
    onetime,
    recurring,
    paymentPlan,
    convenienceFee = 0
  ) => {
    const reqBody = { userId, tier, paymentPlan };

    const tier3Andtier4Threshold = 0.9;

    if (tier === "tier4") {
      // for tier 4 only
      const tmCommission = 0;
      const stripeCommision = 4.5;

      const withRazorpayOnetimeComission =
        tmCommission + convenienceFee + onetime;
      const withRazorpayRecurringComission =
        tmCommission + convenienceFee + recurring;
      const withStripeOnetimeComission =
        tmCommission + convenienceFee + stripeCommision;
      const withStripeRecurringComission =
        tmCommission + convenienceFee + stripeCommision;

      if (!onetime && !recurring) {
        message.error("Enter onetime and recurring payment gateway charges");
        return;
      }
      if (!onetime && onetime !== 0) {
        message.error("Enter onetime payment gateway charges");
        return;
      }
      if (onetime < 0 || onetime > 100) {
        message.error("Enter valid onetime payment gateway charges");
        return;
      }
      if (!recurring && recurring !== 0) {
        message.error("Enter recurring payment gateway charges");
        return;
      }
      if (recurring < 0 || recurring > 100) {
        message.error("Enter valid recurring payment gateway charges");
        return;
      }

      if (
        withRazorpayOnetimeComission < tier3Andtier4Threshold ||
        withRazorpayRecurringComission < tier3Andtier4Threshold ||
        withStripeOnetimeComission < tier3Andtier4Threshold ||
        withStripeRecurringComission < tier3Andtier4Threshold
      ) {
        message.error(
          `Onetime and recurring payment gateway charges should be greater than ${tier3Andtier4Threshold}`
        );
        return;
      }

      reqBody.razorpayOnetimeComission = Number(onetime);
      reqBody.razorpayRecurringComission = Number(recurring);
    }
    const hide = message.loading("Upgrading tier...");
    try {
      const resp = await upgradeTier(reqBody);
      if (resp.code === 0) {
        message.success("Tier upgraded successfully");
        setTierUpgradeModalVisible(false);
        setSelectedUser(null);
        setSelectedTier(null);
        setSelectPaymentPlan(null);
        setRecurringPer(null);
        setOnetimePer(null);
        handleGetWLCreators();
      } else {
        message.error(resp?.message || "Something went wrong");
      }
    } catch (err) {
      console.log(err);
      message.error("Something went wrong");
    } finally {
      hide();
    }
  };

  const handlePlatformSuspend = async (userId, suspend) => {
    const hide = message.loading(
      suspend ? "Suspending platform..." : "Un-suspending platform..."
    );
    try {
      const resp = await suspendPlatform({ userId, suspend });
      if (resp.code === 0) {
        message.success(
          suspend
            ? "Platform suspended successfully"
            : "Platform unsuspended successfully"
        );
        setSuspendPlatformModalVisible(false);
        setSelectedUser(null);
        setPerformSuspendPlatform(false);
        handleGetWLCreators();
      } else {
        message.error(resp?.message || "Something went wrong");
      }
    } catch (err) {
      console.log(err);
      message.error("Something went wrong");
    } finally {
      hide();
    }
  };

  const handleUpdateTierExpiry = async (userId, expiryDate) => {
    const hide = message.loading("Updating tier expiry...");
    try {
      const resp = await updateWLExpiry({ userId, expiryDate });
      if (resp.code === 0) {
        message.success("Tier expiry updated successfully");
        setTierExpiryUpdateModalVisible(false);
        setSelectedUser(null);
        setSelectedTierExpiryDate(null);
        handleGetWLCreators();
      } else {
        message.error(resp?.message || "Something went wrong");
      }
    } catch (err) {
      console.log(err);
      message.error("Something went wrong");
    } finally {
      hide();
    }
  };

  const handleCancelTierSubscription = async (userId) => {
    const hide = message.loading("Cancelling subscription...");
    try {
      const resp = await cancelTierSubscription({ userId });
      if (resp.code === 0) {
        message.success("Subscription cancelled successfully");
      } else {
        message.error(resp?.result || resp?.message || "Something went wrong");
      }
    } catch (err) {
      console.log(err);
      message.error(
        err?.response?.data.result || err?.message || "Something went wrong"
      );
    } finally {
      hide();
      setCancelSubscriptionModalVisible(false);
      setSelectedUser(null);
    }
  };

  const handleCSVUpload = (file) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target.result;
      const lines = text.split("\n");
      const headers = lines[0]
        .split(",")
        .map((h) => h.trim().replace(/"/g, ""));

      // Validate headers
      if (
        headers.length !== 2 ||
        !headers.includes("host") ||
        !headers.includes("accountManagerEmail")
      ) {
        message.error(
          "CSV must contain exactly two columns: host and accountManagerEmail"
        );
        return;
      }

      const data = [];
      // eslint-disable-next-line no-plusplus
      for (let i = 1; i < lines.length; i++) {
        if (lines[i].trim()) {
          const values = lines[i]
            .split(",")
            .map((v) => v.trim().replace(/"/g, ""));
          if (values.length === 2) {
            data.push({
              host: values[0],
              accountManagerEmail: values[1],
            });
          }
        }
      }

      setCsvData(data);
      message.success(`CSV parsed successfully. ${data.length} entries found.`);
    };
    reader.readAsText(file);
    return false; // Prevent default upload behavior
  };

  const handleCSVValidation = async () => {
    if (!csvData || csvData.length === 0) {
      message.error("Please upload a CSV file first");
      return;
    }

    setCsvUploadLoading(true);
    try {
      const resp = await validateBulkAccountManager({ bulkEntries: csvData });

      if (resp.code === 201 || resp.code === 0) {
        setValidationResult(resp.result);

        if (resp.result.errorData && resp.result.errorData.length > 0) {
          message.error(
            "Some entries have errors. Please review and fix them."
          );
        } else if (
          resp.result.errorMessage &&
          resp.result.errorMessage.trim() !== ""
        ) {
          message.error(resp.result.errorMessage);
        } else if (
          resp.result.uploadableData &&
          resp.result.uploadableData.length > 0
        ) {
          message.success("Validation successful! Proceeding with upload...");
          // Proceed with final upload
          await handleFinalUpload(resp.result.uploadableData);
        } else {
          message.error("No valid data found for upload");
        }
      } else {
        message.error(resp?.message || "Validation failed");
      }
    } catch (err) {
      console.log(err);
      message.error("Validation failed. Please try again.");
    } finally {
      setCsvUploadLoading(false);
    }
  };

  const handleFinalUpload = async (uploadableData) => {
    if (!uploadableData || uploadableData.length === 0) {
      message.error("No valid data to upload");
      return;
    }

    setCsvUploadLoading(true);
    try {
      const finalData = uploadableData.map((item) => ({
        creatorId: item.creatorId,
        accountManagerId: item.accountManagerId,
      }));

      const resp = await uploadBulkAccountManager({ bulkEntries: finalData });

      if (resp.code === 201 || resp.code === 0) {
        message.success("Account managers updated successfully!");
        setAccountManagerCSVModalVisible(false);
        setCsvData(null);
        setValidationResult(null);
        handleGetWLCreators(); // Refresh the table
      } else {
        message.error(resp?.message || "Upload failed");
      }
    } catch (err) {
      console.log(err);
      message.error("Upload failed. Please try again.");
    } finally {
      setCsvUploadLoading(false);
    }
  };

  const downloadSampleCSV = () => {
    const sampleData = [
      {
        host: "example1.tagmango.com",
        accountManagerEmail: "manager1@tagmango.com",
      },
      {
        host: "example2.tagmango.com",
        accountManagerEmail: "manager2@tagmango.com",
      },
    ];

    const csvContent = `host,accountManagerEmail\n${sampleData
      .map((row) => `${row.host},${row.accountManagerEmail}`)
      .join("\n")}`;

    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "account-manager-sample.csv";
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const downloadErrorCSV = () => {
    if (
      !validationResult?.errorData ||
      validationResult.errorData.length === 0
    ) {
      message.error("No error data to download");
      return;
    }

    const csvContent = `host,accountManagerEmail,errorMessage\n${validationResult.errorData
      .map(
        (row) =>
          `${row.host},${row.accountManagerEmail},${row.errorMessage || ""}`
      )
      .join("\n")}`;

    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "account-manager-errors.csv";
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const resetCSVModal = () => {
    setCsvData(null);
    setValidationResult(null);
    setCsvUploadLoading(false);
  };

  useEffect(() => {
    const controller = controllerRef.current;
    if (controller?.abort) {
      controller?.abort();
    }
    handleGetWLCreators();
  }, [
    page,
    debouncedSearch,
    startDate,
    endDate,
    earningGt,
    earningLt,
    mangoCountGt,
    mangoCountLt,
    filterTier,
    filterDomainStatus,
    tierExpiryStartDate,
    tierExpiryEndDate,
    tierExpiryStatus,
    platformSuspensionStatus,
    tierAutopayStatus,
    selectedAccountManager,
  ]);

  useEffect(() => {
    dispatch(fetchManagers());
  }, []);

  return (
    <>
      <Row
        gutter={[16, 16]}
        style={{
          marginBottom: 16,
        }}
      >
        <Col span={8}>
          <Card>
            <Statistic title="Total Creators" value={total} />
          </Card>
        </Col>
        <Col span={8}>
          <Card>
            <Statistic
              title="Active"
              value={totalDomainActivated}
              valueStyle={{
                color: COLORS.SUCCESS500,
              }}
            />
          </Card>
        </Col>
        <Col span={8}>
          <Card>
            <Statistic
              title="Suspended"
              value={totalSuspended}
              valueStyle={{
                color: COLORS.ERROR500,
              }}
            />
          </Card>
        </Col>
      </Row>

      <Row
        gutter={[16, 16]}
        style={{
          marginBottom: 16,
        }}
      >
        <Col>
          <CustomSearchbar
            placeholder="Search by name, email or host"
            searchQuery={search}
            setSearchQuery={(value) => {
              setSearch(value);
              setPage(1);
            }}
            style={{ width: 500 }}
          />
        </Col>
        <Col style={{ marginLeft: "auto" }}>
          <Space>
            <Button
              className="siteBtn accentBtnV1"
              onClick={() => setAccountManagerCSVModalVisible(true)}
            >
              <UploadOutlined />
              Upload Account Manager CSV
            </Button>
            <RefreshEarningsButton />
            <Button
              className="siteBtn grayBtn"
              onClick={handleGetWLCreatorsCSV}
              disabled={csvDownloadLoading}
            >
              {csvDownloadLoading ? <Loader /> : <DownloadOutlined />}
              Export CSV
            </Button>
          </Space>
        </Col>
      </Row>

      <Row
        gutter={[16, 16]}
        style={{
          marginBottom: 16,
        }}
      >
        <Col span={8}>
          <Space wrap>
            <Typography>Amount Made:</Typography>
            <InputNumber
              size="large"
              value={earningGt}
              onChange={debounce(setEarningGt)}
            />
            <Typography>To:</Typography>
            <InputNumber
              size="large"
              value={earningLt}
              onChange={debounce(setEarningLt)}
            />
          </Space>
        </Col>
        <Col span={8}>
          <Space>
            <Typography>Number of Mangoes Made:</Typography>
            <InputNumber
              size="large"
              value={mangoCountGt}
              onChange={debounce(setMangoCountGt)}
            />
            <Typography>To:</Typography>
            <InputNumber
              size="large"
              value={mangoCountLt}
              onChange={debounce(setMangoCountLt)}
            />
          </Space>
        </Col>
        <Col
          span={8}
          style={{
            height: "100%",
          }}
        >
          <Space>
            <DatePicker.RangePicker
              size="large"
              ranges={DEFAULT_DATES_RANGE_OPTIONS}
              value={[startDate, endDate]}
              onChange={(values) => {
                setStartDate(values?.[0]);
                setEndDate(values?.[1]);
              }}
            />
          </Space>
        </Col>
      </Row>
      <Row
        gutter={[16, 16]}
        style={{
          marginBottom: 16,
        }}
      >
        <Col span={24}>
          <Space size={12} wrap>
            <Select
              size="large"
              placeholder="Select tier"
              allowClear
              value={filterTier}
              onSelect={setFilterTier}
              onClear={() => setFilterTier(null)}
            >
              <Option value="tier1">Tier 1</Option>
              <Option value="tier2">Tier 2</Option>
              <Option value="tier3">Tier 3</Option>
              <Option value="tier4">Tier 4</Option>
            </Select>
            <Select
              size="large"
              placeholder="Select domain status"
              allowClear
              value={filterDomainStatus}
              onSelect={setFilterDomainStatus}
              onClear={() => setFilterDomainStatus(null)}
            >
              <Option value="domain_verified">Domain Verified</Option>
              <Option value="initiated">Initiated</Option>
              <Option value="drafted">Drafted</Option>
            </Select>
            <DatePicker.RangePicker
              size="large"
              ranges={DEFAULT_DATES_RANGE_OPTIONS}
              placeholder={["Sub. End Start Date", "Sub. End End Date"]}
              allowClear
              value={[tierExpiryStartDate, tierExpiryEndDate]}
              onChange={(values) => {
                setTierExpiryStartDate(values?.[0]);
                setTierExpiryEndDate(values?.[1]);
              }}
              style={{ width: 350 }}
            />
            <Select
              size="large"
              placeholder="Select tier expiry status"
              allowClear
              value={tierExpiryStatus}
              onSelect={setTierExpiryStatus}
              onClear={() => setTierExpiryStatus(null)}
            >
              <Option value="expired">Expired</Option>
              <Option value="notexpired">Not Expired</Option>
            </Select>
            <Select
              size="large"
              placeholder="Select platform suspension status"
              allowClear
              value={platformSuspensionStatus}
              onSelect={setPlatformSuspensionStatus}
              onClear={() => setPlatformSuspensionStatus(null)}
            >
              <Option value="suspended">Suspended</Option>
              <Option value="notsuspended">Not Suspended</Option>
            </Select>
            <Select
              size="large"
              placeholder="Select tier autopay status"
              allowClear
              value={tierAutopayStatus}
              onSelect={setTierAutopayStatus}
              onClear={() => setTierAutopayStatus(null)}
            >
              {autopayStatuses.map((val) => (
                <Option value={val}>{val}</Option>
              ))}
            </Select>
            <Select
              size="large"
              placeholder="Select account manager"
              allowClear
              value={selectedAccountManager}
              onSelect={setSelectedAccountManager}
              onClear={() => setSelectedAccountManager(null)}
            >
              {managers.map((manager) => (
                <Option key={manager._id} value={manager._id}>
                  {manager.name} ({manager.email})
                </Option>
              ))}
            </Select>
          </Space>
        </Col>
      </Row>
      {creatorsTable()}
      <Modal
        visible={tierUpgradeModalVisible}
        onCancel={() => {
          setTierUpgradeModalVisible(false);
          setSelectedUser(null);
          setSelectedTier(null);
          setRecurringPer(null);
          setOnetimePer(null);
        }}
        footer={null}
        width={800}
        destroyOnClose
      >
        <Typography>
          Are you sure you want to upgrade the plan for {selectedUser?.name}?
        </Typography>
        <Space
          direction="vertical"
          style={{
            width: "100%",
          }}
        >
          <Select
            style={{
              width: "200px",
            }}
            value={selectedTier}
            onChange={(value) => setSelectedTier(value)}
          >
            <Option value="tier1">Tier 1</Option>
            <Option value="tier2">Tier 2</Option>
            <Option value="tier3">Tier 3</Option>
            <Option value="tier4">Tier 4</Option>
          </Select>
          {selectedTier === "tier4" ? (
            <>
              <Typography>TM Commission: 0%</Typography>
              <Typography>Stripe Commission: 4.5%</Typography>
              <Typography>Indian PG charges: </Typography>

              <Typography>
                {" "}
                onetime % :{" "}
                <InputNumber value={onetimePer} onChange={setOnetimePer} />
              </Typography>

              <Typography>
                {" "}
                recurring % :{" "}
                <InputNumber value={recurringPer} onChange={setRecurringPer} />
              </Typography>
            </>
          ) : null}

          <Typography>Payment Plan</Typography>
          <Select
            style={{
              width: "200px",
            }}
            value={selectPaymentPlan}
            onChange={(value) => setSelectPaymentPlan(value)}
          >
            <Option value="monthly">Monthly</Option>
            <Option value="annual">annual</Option>
          </Select>

          <Space>
            <Button
              type="primary"
              onClick={() => {
                if (selectedTier) {
                  handleTierUpgrade(
                    selectedUser?._id,
                    selectedTier,
                    onetimePer,
                    recurringPer,
                    selectPaymentPlan,
                    selectedUser?.convenienceFee
                  );
                } else {
                  message.error("Please select a plan");
                }
              }}
            >
              Yes
            </Button>
            <Button
              onClick={() => {
                setTierUpgradeModalVisible(false);
                setSelectedUser(null);
                setSelectedTier(null);
                setRecurringPer(null);
                setOnetimePer(null);
              }}
            >
              No
            </Button>
          </Space>
        </Space>
      </Modal>
      <Modal
        visible={suspendPlatformModalVisible}
        onCancel={() => {
          setSuspendPlatformModalVisible(false);
          setSelectedUser(null);
        }}
        footer={null}
        width={800}
        destroyOnClose
      >
        <Typography>
          Are you sure you want to{" "}
          {performSuspendPlatform ? "suspend" : "un-suspend"} the platform for{" "}
          {selectedUser?.name}?
        </Typography>
        <Space>
          <Button
            type="primary"
            onClick={() => {
              handlePlatformSuspend(selectedUser?._id, performSuspendPlatform);
            }}
          >
            Yes
          </Button>
          <Button
            onClick={() => {
              setTierUpgradeModalVisible(false);
              setSelectedUser(null);
            }}
          >
            No
          </Button>
        </Space>
      </Modal>
      <Modal
        visible={tierExpiryUpdateModalVisible}
        onCancel={() => {
          setTierExpiryUpdateModalVisible(false);
          setSelectedUser(null);
        }}
        footer={null}
        width={800}
        destroyOnClose
      >
        <Typography>
          Are you sure you want to update the tier expiry for{" "}
          {selectedUser?.name}?
        </Typography>
        <Space direction="vertical">
          <DatePicker
            value={selectedTierExpiryDate}
            onChange={(value) => setSelectedTierExpiryDate(value)}
            placeholder="Tier Expiry Date"
            style={{
              width: "200px",
            }}
          />
          <Space>
            <Button
              type="primary"
              onClick={() => {
                handleUpdateTierExpiry(
                  selectedUser?._id,
                  selectedTierExpiryDate
                );
              }}
            >
              Save
            </Button>
            <Button
              onClick={() => {
                setTierUpgradeModalVisible(false);
                setSelectedUser(null);
                setSelectedTierExpiryDate(null);
              }}
            >
              Cancel
            </Button>
          </Space>
        </Space>
      </Modal>

      <ManagePlanModal
        visible={managePlanModalVisible}
        onClose={() => setManagePlanModalVisible(false)}
        onSave={() => {
          handleGetWLCreators();
          setManagePlanModalVisible(false);
        }}
        userId={selectedUser?._id}
        mode="normal"
      />

      <Modal
        visible={cancelSubscriptionModalVisible}
        onCancel={() => {
          setSelectedUser(null);
          setCancelSubscriptionModalVisible(false);
        }}
        onOk={() => {
          handleCancelTierSubscription(selectedUser?._id);
        }}
        destroyOnClose
        title="Cancel Subscription?"
      >
        Are you sure you want to cancel the subscription for{" "}
        {selectedUser?.name}? THIS IS IRREVERSIBLE
      </Modal>

      <ManageTagUntagPartnerModal
        visible={partnerTagUntagModalVisible}
        onClose={() => setPartnerTagUntagModalVisible(false)}
        onSave={() => {
          setPartnerTagUntagModalVisible(false);
        }}
        userId={selectedUser?._id}
      />

      <UpdateGstDetailsModal
        visible={gstDetailsModalVisible}
        onClose={() => setGstDetailsModalVisible(false)}
        creatorId={selectedUser?._id}
        creatorName={selectedUser?.name}
        user={selectedUser}
        onSuccess={() => {
          handleGetWLCreators();
        }}
      />

      {/* CSV Upload Modal */}
      <Modal
        visible={accountManagerCSVModalVisible}
        onCancel={() => {
          setAccountManagerCSVModalVisible(false);
          resetCSVModal();
        }}
        footer={null}
        width={800}
        destroyOnClose
        title="Upload Account Manager CSV"
      >
        <Space direction="vertical" style={{ width: "100%" }} size="large">
          <div>
            <Typography.Title level={5}>Instructions:</Typography.Title>
            <Typography.Text>
              Upload a CSV file with exactly two columns: <strong>host</strong>{" "}
              and <strong>accountManagerEmail</strong>
            </Typography.Text>
            <br />
            <Button
              type="link"
              onClick={downloadSampleCSV}
              icon={<DownloadOutlined />}
            >
              Download Sample CSV
            </Button>
          </div>

          <div>
            <Typography.Title level={5}>Upload CSV:</Typography.Title>
            <Upload
              accept=".csv"
              beforeUpload={handleCSVUpload}
              showUploadList={false}
            >
              <Button icon={<UploadOutlined />} loading={csvUploadLoading}>
                Choose CSV File
              </Button>
            </Upload>
            {csvData && (
              <Typography.Text style={{ marginLeft: 16 }}>
                ✓ {csvData.length} entries loaded
              </Typography.Text>
            )}
          </div>

          {csvData && csvData.length > 0 && (
            <div>
              <Button
                type="primary"
                onClick={handleCSVValidation}
                loading={csvUploadLoading}
                disabled={!csvData || csvData.length === 0}
              >
                Validate & Upload
              </Button>
            </div>
          )}

          {validationResult && (
            <div>
              <Typography.Title level={5}>Validation Results:</Typography.Title>

              {validationResult.errorData &&
                validationResult.errorData.length > 0 && (
                  <div style={{ marginBottom: 16 }}>
                    <Typography.Text type="danger">
                      {validationResult.errorData.length} entries have errors
                    </Typography.Text>
                    <Button
                      type="link"
                      onClick={downloadErrorCSV}
                      icon={<DownloadOutlined />}
                      danger
                    >
                      Download Error Entries
                    </Button>
                  </div>
                )}

              {validationResult.uploadableData &&
                validationResult.uploadableData.length > 0 && (
                  <div>
                    <Typography.Text type="success">
                      {validationResult.uploadableData.length} entries are valid
                      and ready for upload
                    </Typography.Text>
                  </div>
                )}

              {validationResult.errorMessage && (
                <Typography.Text type="danger">
                  {validationResult.errorMessage}
                </Typography.Text>
              )}
            </div>
          )}
        </Space>
      </Modal>
    </>
  );
};

EnterpriseCreators.propTypes = {
  sensitiveAllowedActions: PropTypes.shape([]),
};

export default EnterpriseCreators;
